/**
 * Client-side file preparation for Studio attachments.
 *
 * The server accepts png, jpeg, webp, gif and pdf up to 15 MB, sniffed from
 * magic bytes. HEIC/HEIF (the iPhone default) is not on that list, so it is
 * re-encoded to JPEG in the browser first. Oversized jpeg/png/webp are also
 * re-encoded, scaled down so the long edge is at most 4096 px.
 */

/** Server limit per file (15 MB). */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

const MAX_LONG_EDGE_PX = 4096;
const JPEG_QUALITY = 0.9;
const DECODE_ERROR = "Couldn't read this photo. Try sending it as a JPEG.";

const REENCODABLE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);
const HEIF_TYPES = new Set(["image/heic", "image/heif"]);
const HEIF_NAME = /\.(heic|heif)$/i;

interface FileLike {
  type: string;
  name: string;
  size: number;
}

function isHeif(file: Pick<FileLike, "type" | "name">): boolean {
  return HEIF_TYPES.has(file.type) || HEIF_NAME.test(file.name);
}

/** True when the file must be re-encoded before upload. */
export function needsReencode(file: FileLike): boolean {
  if (isHeif(file)) return true;
  return REENCODABLE_TYPES.has(file.type) && file.size > MAX_UPLOAD_BYTES;
}

/** Any image, including HEIC (which some browsers report with an empty type). */
export function isImageFile(file: Pick<FileLike, "type" | "name">): boolean {
  return file.type.startsWith("image/") || isHeif(file);
}

/** Types the picker, drop zone and paste handler will take. Server still has the final say. */
export function isAttachable(file: Pick<FileLike, "type" | "name">): boolean {
  return isImageFile(file) || file.type === "application/pdf" || /\.pdf$/i.test(file.name);
}

function jpegName(name: string): string {
  const stem = name.replace(/\.[^.]*$/, "");
  return `${stem || "photo"}.jpg`;
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas produced no image"))),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
}

/**
 * Return the file as-is when it can be uploaded directly; otherwise decode it,
 * draw it to a canvas (long edge <= 4096 px) and return a new JPEG File.
 * Any decode or encode failure rejects with a user-facing message.
 */
export async function prepareUpload(file: File): Promise<File> {
  if (!needsReencode(file)) return file;

  try {
    const bitmap = await createImageBitmap(file);
    try {
      const scale = Math.min(1, MAX_LONG_EDGE_PX / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("No 2D canvas context");
      ctx.drawImage(bitmap, 0, 0, width, height);

      const blob = await canvasToJpeg(canvas);
      return new File([blob], jpegName(file.name), {
        type: "image/jpeg",
        lastModified: file.lastModified,
      });
    } finally {
      bitmap.close();
    }
  } catch {
    throw new Error(DECODE_ERROR);
  }
}
